import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useDropzone } from "react-dropzone";

import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import { UserContext } from "@/app/providers";
import { ShiftEventRequest } from "@/features/shift/model/Shift/Shift";
import { shiftLogEventDataValidation } from "@/features/shift/model/validators";
import CustomCheckbox from "@/shared/forms/CustomCheckbox";
import CustomTextAreaArray from "@/shared/forms/CustomTextareaArray";
import NativeDateTimeField from "@/shared/forms/NativeDateTimeField";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { ErrorToast, SuccessToast } from "@/shared/notifications/AlertToast";
import { CloseActionButton, SaveActionButton } from "@/shared/ui";
import {
	sharedShiftDialogActionsSx,
	sharedShiftDialogContentSx,
	sharedShiftDialogHeaderSx,
	sharedShiftDialogPanelSx,
	sharedShiftDialogPaperSx,
} from "@/styles/sharedShiftDialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
	Box,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";

import { ShiftName } from "@/model/Application/Settings/SettingsResponseTypes";

import {
	FetchShiftOptionsResult,
	useFetchShiftNames,
	useFetchShiftOptions,
} from "@/service/ShiftHomeQueries";
import { useMutateCreateShiftEvent } from "@/service/ShiftMutations";

dayjs.extend(utc);
dayjs.extend(timezone);

type Props = {
	page?: string;
	open: boolean;
	onClose: () => void;
	selectedGroup: string | number | null | undefined;
};

type ShiftRegisterValues = ShiftEventRequest & {
	id: number;
	date: string;
	description: string;
	systems: number[];
	groups: number[];
	tags: string[];
	shiftId: number | null;
	shiftCrewId: number | null;
	dayNightId: number | null;
	systemId: number;
	groupId: number;
	file: File | null;
	filename: string;
	filepath: string;
	isHandoverEvent: boolean;
	isOutOfService: boolean;
};

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const parseTimeToSeconds = (
	value: string | null | undefined,
): number | null => {
	if (!value) return null;

	const parts = value.split(":").map((part) => Number(part));
	if (
		parts.length < 2 ||
		parts.length > 3 ||
		parts.some((part) => Number.isNaN(part))
	) {
		return null;
	}

	const [hours, minutes, seconds = 0] = parts;
	if (
		hours < 0 ||
		hours > 23 ||
		minutes < 0 ||
		minutes > 59 ||
		seconds < 0 ||
		seconds > 59
	) {
		return null;
	}

	return hours * 3600 + minutes * 60 + seconds;
};

const matchesShiftWindow = (
	currentSeconds: number,
	startSeconds: number,
	endSeconds: number,
): boolean => {
	if (startSeconds === endSeconds) return true;
	if (startSeconds < endSeconds) {
		return currentSeconds >= startSeconds && currentSeconds < endSeconds;
	}

	return currentSeconds >= startSeconds || currentSeconds < endSeconds;
};

const getDefaultShiftRotationId = (
	dateValue: string,
	shiftNames: ShiftName[],
): number | null => {
	if (!shiftNames.length) return null;

	const localDate = dayjs(dateValue);
	const effectiveDate = localDate.isValid() ? localDate : dayjs();
	const currentSeconds =
		effectiveDate.hour() * 3600 +
		effectiveDate.minute() * 60 +
		effectiveDate.second();

	for (const shiftName of shiftNames) {
		const startSeconds = parseTimeToSeconds(shiftName.StartTime);
		const endSeconds = parseTimeToSeconds(shiftName.EndTime);

		if (startSeconds == null || endSeconds == null) continue;
		if (matchesShiftWindow(currentSeconds, startSeconds, endSeconds)) {
			return shiftName.Id;
		}
	}

	return null;
};

const requiredFieldsSchema = Yup.object({
	description: Yup.string().trim().required("Description is required"),

	systems: Yup.array()
		.of(Yup.number())
		.min(1, "At least one system is required")
		.required("At least one system is required"),

	groups: Yup.array()
		.of(Yup.number())
		.min(1, "At least one group is required")
		.required("At least one group is required"),
});

// If the base validation exists, merge them.
// Otherwise fallback to our required schema only.
const optionalOverrides = Yup.object({
	shiftId: Yup.number()
		.transform((value: unknown) => {
			const numberValue = typeof value === "number" ? value : Number(value);
			return Number.isNaN(numberValue) ? null : numberValue;
		})
		.nullable()
		.notRequired(),

	shiftCrewId: Yup.number()
		.transform((value: unknown) => {
			const numberValue = typeof value === "number" ? value : Number(value);
			return Number.isNaN(numberValue) ? null : numberValue;
		})
		.nullable()
		.notRequired(),

	dayNightId: Yup.number()
		.transform((value: unknown) => {
			const numberValue = typeof value === "number" ? value : Number(value);
			return Number.isNaN(numberValue) ? null : numberValue;
		})
		.nullable()
		.notRequired(),
});

const combinedValidationSchema = shiftLogEventDataValidation
	.concat(requiredFieldsSchema)
	.concat(optionalOverrides);

const ShiftRegisterDialog: React.FC<Props> = ({
	page,
	open,
	onClose,
	selectedGroup,
}) => {
	const { createShiftEvent, isSubmitting: isCreatingShiftEvent } =
		useMutateCreateShiftEvent(page);
	const user = useContext(UserContext);
	const { data: shiftOptions } =
		useFetchShiftOptions() as FetchShiftOptionsResult;
	const { data: shiftNames, isLoading: isLoadingShiftNames } =
		useFetchShiftNames();
	const { currentTime, timezone: tz } = useTime();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [openedAtTime, setOpenedAtTime] = useState<string | null>(null);

	// derive selectedGroupId once (memoized)
	const selectedGroupIdFromProp = useMemo<number>(() => {
		if (
			selectedGroup === "all" ||
			selectedGroup === "" ||
			selectedGroup == null
		)
			return NaN;
		const asNum = Number(selectedGroup);
		return Number.isFinite(asNum) ? asNum : NaN;
	}, [selectedGroup]);

	// groups the user belongs to
	const filteredGroups = useMemo(() => {
		if (!shiftOptions?.Groups || !user?.Groups) return [];
		const userGroupIds = new Set(user.Groups.map((g) => String(g.Id)));
		return shiftOptions.Groups.filter((g) => userGroupIds.has(String(g.Id)));
	}, [shiftOptions, user]);

	// effective list of groups to show: user groups if any, otherwise all groups
	const effectiveGroups = useMemo(() => {
		if (filteredGroups.length > 0) return filteredGroups;
		return shiftOptions?.Groups ?? [];
	}, [filteredGroups, shiftOptions]);

	// initial values builder
	const buildInitialValues = useCallback((): ShiftRegisterValues => {
		const defaultSystemId = shiftOptions?.Systems?.[0]?.Id ?? NaN;
		const defaultDate = openedAtTime ?? dayjs.utc(currentTime).tz(tz).format();
		const defaultShiftId = getDefaultShiftRotationId(
			defaultDate,
			shiftNames ?? [],
		);

		// use selectedGroup if provided, otherwise first from effectiveGroups
		const propGroupId =
			Number.isFinite(selectedGroupIdFromProp) &&
			!Number.isNaN(selectedGroupIdFromProp)
				? selectedGroupIdFromProp
				: NaN;

		const defaultGroupId =
			propGroupId ||
			effectiveGroups?.[0]?.Id ||
			user?.Groups?.[0]?.Id ||
			shiftOptions?.Groups?.[0]?.Id ||
			NaN;

		return {
			id: NaN,
			date: defaultDate,
			shiftId: defaultShiftId ?? null,
			systemId: defaultSystemId,
			description: "",
			filename: "",
			filepath: "",
			isHandoverEvent: false,
			isOutOfService: false,
			groupId: defaultGroupId,
			systems: defaultSystemId ? [defaultSystemId] : [],
			groups: defaultGroupId ? [defaultGroupId] : [],
			tags: [],
			shiftCrewId: user?.ShiftCrew ?? null,
			dayNightId: null,
			file: null,
		};
	}, [
		shiftOptions,
		user,
		currentTime,
		tz,
		openedAtTime,
		selectedGroupIdFromProp,
		effectiveGroups,
		shiftNames,
	]);

	const [initialValues, setInitialValues] =
		useState<ShiftRegisterValues | null>(null);

	useEffect(() => {
		if (!open) {
			setOpenedAtTime(null);
			setInitialValues(null);
			return;
		}

		setOpenedAtTime(
			(previous) => previous ?? dayjs.utc(currentTime).tz(tz).format(),
		);
	}, [open, currentTime, tz]);

	useEffect(() => {
		if (!open) return;
		if (!shiftOptions || !user) return;
		if (isLoadingShiftNames) return;
		if (!openedAtTime) return;
		if (initialValues) return;
		setInitialValues(buildInitialValues());
	}, [
		open,
		shiftOptions,
		user,
		buildInitialValues,
		isLoadingShiftNames,
		openedAtTime,
		initialValues,
	]);

	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	const handleSubmit = async (
		values: ShiftRegisterValues,
		formikHelpers: FormikHelpers<ShiftRegisterValues>,
	) => {
		formikHelpers.setSubmitting(true);

		const finalSystems = Array.isArray(values.systems) ? values.systems : [];
		const finalGroups = Array.isArray(values.groups) ? values.groups : [];

		const payload: ShiftEventRequest = {
			...values,
			shiftId: values.shiftId ?? undefined,
			dayNightId: values.shiftId ?? values.dayNightId ?? undefined,
			shiftCrewId: values.shiftCrewId ?? undefined,
			systems: finalSystems,
			groups: finalGroups,
			systemId: finalSystems[0] ?? values.systemId ?? NaN,
			groupId: finalGroups[0] ?? values.groupId ?? NaN,
			file: values.file ?? undefined,
			filename: values.file?.name ?? values.filename ?? undefined,
			date: dayjs(values.date).utc().format(),
			id: 0,
		};

		try {
			await createShiftEvent(payload);
			void SuccessToast(
				<Translate String="ShiftRegister.Alert.Success.Create">
					Shift event created successfully
				</Translate>,
			);
		} catch (error) {
			console.error("Create shift event error:", error);
			ErrorToast(
				<Translate String="ShiftRegister.Alert.Error.Create">
					{`Shift event creation error ${String(error)}`}
				</Translate>,
			);
		} finally {
			formikHelpers.setSubmitting(false);
			handleClose();
		}
	};

	if (!initialValues) return null;

	return (
		<Dialog
			maxWidth="sm"
			scroll="paper"
			fullScreen={isMobile}
			open={open}
			onClose={handleClose}
			aria-labelledby="shift-register-dialog-title"
			PaperProps={{
				sx: {
					...sharedShiftDialogPaperSx(theme, isMobile),
					maxHeight: isMobile ? "100vh" : "min(90vh, 980px)",
					display: "flex",
					flexDirection: "column",
				},
			}}
		>
			<Formik
				initialValues={initialValues}
				validationSchema={combinedValidationSchema}
				onSubmit={handleSubmit}
			>
				{({ values, errors, touched, setFieldValue, isSubmitting }) => {
					const isSaving = isSubmitting || isCreatingShiftEvent;
					const dropzone = useDropzone({
						onDrop: (acceptedFiles: File[]) => {
							const f = acceptedFiles[0];
							if (f) void setFieldValue("file", f);
						},
						onDropRejected: (rejections) => {
							const err = rejections[0]?.errors?.[0];
							if (err?.code === "file-too-large") {
								ErrorToast(
									<Translate String="Shift.Event.FileTooLarge">
										File is too large (max 5 MB)
									</Translate>,
								);
							} else if (err?.code === "file-invalid-type") {
								ErrorToast(
									<Translate String="Shift.Event.FileTypeNotAllowed">
										File type not allowed
									</Translate>,
								);
							}
						},
						accept: {
							"application/pdf": [".pdf"],
							"application/msword": [".doc"],
							"application/vnd.openxmlformats-officedocument.wordprocessingml.document":
								[".docx"],
							"image/jpeg": [".jpg", ".jpeg"],
							"image/png": [".png"],
						},
						maxFiles: 1,
						maxSize: MAX_FILE_SIZE_BYTES,
						multiple: false,
						disabled: isSaving,
					});
					return (
						<Form
							style={{
								display: "flex",
								flexDirection: "column",
								flex: 1,
								minHeight: 0,
								overflow: "hidden",
							}}
						>
							<DialogTitle
								id="shift-register-dialog-title"
								component="div"
								sx={{
									...sharedShiftDialogHeaderSx(theme),
									position: "relative",
									px: { xs: 1.5, sm: 2.5 },
									pt: 2,
									pb: 1.5,
								}}
							>
								<Typography
									variant="h6"
									component="h2"
									sx={{ fontWeight: 700, color: "text.primary", pr: 5 }}
								>
									<Translate String="Shift.Event.RegisteringEvent">
										Registering Shift Event
									</Translate>
								</Typography>
								<IconButton
									aria-label="Close"
									onClick={handleClose}
									size="small"
									sx={{
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 2,
										color: "text.secondary",
										bgcolor: "background.paper",
										"&:hover": {
											color: "text.primary",
											backgroundColor: "action.hover",
										},
									}}
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							</DialogTitle>

							<DialogContent
								dividers
								sx={{
									...sharedShiftDialogContentSx(theme),
									px: { xs: 1.5, sm: 2.5 },
									pt: 2,
									pb: 2,
									overflowY: "auto",
									WebkitOverflowScrolling: "touch",
									flex: 1,
									minHeight: 0,
									...(isMobile && { pb: 3 }),
								}}
							>
								<Grid
									container
									spacing={2}
									justifyContent="flex-start"
									alignItems="flex-start"
									sx={{
										"& .MuiOutlinedInput-root": {
											borderRadius: 2,
										},
									}}
								>
									{/* Date */}
									<Grid item xs={12} sm={12} sx={{ mb: 2 }}>
										<NativeDateTimeField
											label={
												<Translate String="Shift.Event.Date">Date</Translate>
											}
											name="date"
											id="date"
										/>
									</Grid>

									{/* Day/Night (Shift Type) */}
									<Grid item xs={12} sm={6} sx={{ mb: 1 }}>
										<FormControl fullWidth size="small">
											<InputLabel id="shiftType-label">
												<Translate String="Shift.Event.ShiftRotation">
													Shift Rotation
												</Translate>
											</InputLabel>
											<Select
												labelId="shiftType-label"
												label={
													<Translate String="Shift.Event.ShiftRotation">
														Shift Rotation
													</Translate>
												}
												id="shiftId"
												value={values.shiftId ?? ""}
												onChange={(e) =>
													void setFieldValue(
														"shiftId",
														e.target.value === ""
															? null
															: Number(e.target.value),
													)
												}
											>
												<MenuItem value="">
													<em>None</em>
												</MenuItem>
												{shiftOptions?.ShiftTypes?.map((st) => (
													<MenuItem key={st.Id} value={st.Id}>
														{st.Name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>

									{/* Shift Crew */}
									<Grid item xs={12} sm={6}>
										<FormControl fullWidth size="small">
											<InputLabel id="crew-label">
												<Translate String="Shift.Event.CrewName">
													Crew Name
												</Translate>
											</InputLabel>
											<Select
												labelId="crew-label"
												id="shiftCrewId"
												label={
													<Translate String="Shift.Event.CrewName">
														Crew Name
													</Translate>
												}
												value={values.shiftCrewId ?? ""}
												onChange={(e) =>
													void setFieldValue(
														"shiftCrewId",
														e.target.value ? Number(e.target.value) : null,
													)
												}
											>
												<MenuItem value="">
													<em>None</em>
												</MenuItem>
												{shiftOptions?.Crews?.map((c) => (
													<MenuItem key={c.Id} value={c.Id}>
														{c.Name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>

									{/* Systems (multiple) */}
									<Grid item xs={12} sm={6}>
										<FormControl
											fullWidth
											error={Boolean(touched.systems && errors.systems)}
										>
											<InputLabel id="systems-multi-label">
												<Translate String="Shift.Event.Systems">
													Systems
												</Translate>
											</InputLabel>
											<Select
												labelId="systems-multi-label"
												id="systems"
												multiple
												label={
													<Translate String="Shift.Event.Systems">
														Systems
													</Translate>
												}
												value={values.systems ?? []}
												onChange={(e) => {
													const raw = Array.isArray(e.target.value)
														? e.target.value
														: [e.target.value];
													const ids = raw.map((r) => Number(r));
													const unique = Array.from(new Set(ids));
													void setFieldValue("systems", unique);
												}}
												renderValue={(selected) => (
													<Box
														sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
													>
														{(Array.isArray(selected) ? selected : []).map(
															(id) => {
																const numericId = Number(id);
																const item = shiftOptions?.Systems?.find(
																	(s) => s.Id === id,
																);
																return item ? (
																	<Chip
																		key={numericId}
																		label={item.Name}
																		size="small"
																	/>
																) : null;
															},
														)}
													</Box>
												)}
												size="small"
												MenuProps={{
													PaperProps: {
														elevation: 3,
														sx: {
															borderRadius: 2,
															maxHeight: isMobile
																? "min(400px, 60vh)"
																: undefined,
														},
													},
												}}
											>
												{shiftOptions?.Systems?.map((item) => (
													<MenuItem key={item.Id} value={item.Id}>
														{item.Name}
													</MenuItem>
												))}
											</Select>
											{touched.systems && errors.systems && (
												<Typography variant="caption" color="error">
													{String(errors.systems)}
												</Typography>
											)}
										</FormControl>
									</Grid>

									{/* Groups (multiple) */}
									<Grid item xs={12} sm={6}>
										<FormControl
											fullWidth
											size="small"
											error={Boolean(touched.groups && errors.groups)}
										>
											<InputLabel id="groups-multi-label">
												<Translate String="Shift.Event.Groups">
													Groups
												</Translate>
											</InputLabel>
											<Select
												labelId="groups-multi-label"
												id="groups"
												multiple
												label={
													<Translate String="Shift.Event.Groups">
														Groups
													</Translate>
												}
												value={values.groups ?? []}
												onChange={(e) => {
													const raw = Array.isArray(e.target.value)
														? e.target.value
														: [e.target.value];
													const ids = raw.map((r) => Number(r));
													const unique = Array.from(new Set(ids));
													void setFieldValue("groups", unique);
												}}
												renderValue={(selected) => (
													<Box
														sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
													>
														{(Array.isArray(selected) ? selected : []).map(
															(id) => {
																const numericId = Number(id);
																const item = effectiveGroups.find(
																	(g) => g.Id === numericId,
																);
																return item ? (
																	<Chip
																		key={numericId}
																		label={item.Name}
																		size="small"
																	/>
																) : null;
															},
														)}
													</Box>
												)}
												size="small"
												MenuProps={{
													PaperProps: {
														elevation: 3,
														sx: {
															borderRadius: 2,
															maxHeight: isMobile
																? "min(400px, 60vh)"
																: undefined,
														},
													},
												}}
											>
												{effectiveGroups.map((item) => (
													<MenuItem key={item.Id} value={item.Id}>
														{item.Name}
													</MenuItem>
												))}
											</Select>
											{touched.groups && errors.groups && (
												<Typography variant="caption" color="error">
													{String(errors.groups)}
												</Typography>
											)}
										</FormControl>
									</Grid>

									{/* Description */}
									<Grid item xs={12}>
										<SATOSEditorField
											id="description"
											name="description"
											label="Description"
											aria-required="true"
											aria-describedby="description-error-text"
											style={{ minWidth: "100%" }}
											imageUploadDisabled
										/>
									</Grid>

									{/* Tags */}
									<Grid item xs={12} sm={4}>
										<Typography variant="body1" sx={{ mb: 1 }}>
											<Translate String="Shift.Event.Tags">Tags</Translate>
										</Typography>
										<CustomTextAreaArray
											label=""
											name="tags"
											id="tags"
											type="text"
											fullWidth
										/>
									</Grid>

									{/* Handover */}
									<Grid item xs={6} sm={4}>
										<Typography variant="body1" sx={{ mb: 1 }}>
											<Translate String="Shift.Event.HandoverEvent">
												Handover event
											</Translate>
										</Typography>
										<CustomCheckbox
											label=""
											name="isHandoverEvent"
											id="isHandoverEvent"
										/>
									</Grid>

									{/* Out of service */}
									<Grid item xs={6} sm={4}>
										<Typography variant="body1" sx={{ mb: 1 }}>
											<Translate String="Shift.Event.OutOfService">
												Out of service
											</Translate>
										</Typography>
										<CustomCheckbox
											label=""
											name="isOutOfService"
											id="isOutOfService"
										/>
									</Grid>

									{/* File upload */}
									<Grid item xs={12}>
										<Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
											<Translate String="Shift.Event.Attachment">
												Attachment
											</Translate>
										</Typography>
										<Box
											{...dropzone.getRootProps()}
											sx={{
												...sharedShiftDialogPanelSx(theme),
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												justifyContent: "center",
												border: "2px dashed",
												borderColor: dropzone.isDragActive
													? "primary.main"
													: "divider",
												py: 3,
												px: 2,
												textAlign: "center",
												cursor: "pointer",
												bgcolor: dropzone.isDragActive
													? "action.hover"
													: "background.default",
												color: "text.secondary",
												transition: "border-color 0.2s, background-color 0.2s",
												"&:hover": {
													borderColor: "primary.main",
													bgcolor: "action.hover",
												},
											}}
										>
											<input
												{...dropzone.getInputProps()}
												id="shift-register-file-input"
											/>
											{values.file ? (
												<Stack
													direction="row"
													alignItems="center"
													spacing={2}
													sx={{
														p: 2,
														borderRadius: 1,
														bgcolor: "background.paper",
														width: "100%",
														maxWidth: 400,
														border: "1px solid",
														borderColor: "divider",
													}}
												>
													<InsertDriveFileOutlinedIcon
														sx={{ color: "text.secondary", fontSize: 32 }}
													/>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Typography variant="body2" fontWeight={600} noWrap>
															{values.file.name}
														</Typography>
													</Box>
													<IconButton
														onClick={(e) => {
															e.stopPropagation();
															void setFieldValue("file", null);
														}}
														color="error"
														size="small"
														aria-label="Remove file"
														disabled={isSaving}
													>
														<CloseIcon fontSize="small" />
													</IconButton>
												</Stack>
											) : (
												<>
													<Typography variant="body2" sx={{ mb: 0.5 }}>
														<Translate String="Shift.Event.Dropzone.Drag">
															Drag a file here or click to browse
														</Translate>
													</Typography>
													<Typography variant="caption">
														<Translate String="Shift.Event.File.Help">
															PDF, DOC, JPG, PNG · max 5 MB
														</Translate>
													</Typography>
												</>
											)}
										</Box>
									</Grid>
								</Grid>
							</DialogContent>

							<DialogActions
								sx={{
									...sharedShiftDialogActionsSx(theme, isMobile),
									px: { xs: 1.5, sm: 2.5 },
									py: 1.75,
									mt: 0,
									position: "sticky",
									bottom: 0,
									zIndex: 1,
									backgroundColor: "background.paper",
									display: "flex",
									justifyContent: "flex-end",
									alignItems: "center",
									flexWrap: "wrap",
									gap: 1.5,
									flexShrink: 0,
									...(isMobile && {
										boxShadow: "0 -2px 10px rgba(0,0,0,0.08)",
									}),
								}}
								>
									<CloseActionButton
										onClick={handleClose}
										disabled={isSaving}
										sx={{
											minHeight: { xs: 44, sm: 36 },
										}}
									>
										<Translate String="Shift.Event.Close">Close</Translate>
									</CloseActionButton>
									<SaveActionButton
										autoFocus
										type="submit"
										loading={isSaving}
										loadingText={
											<Translate String="ShiftRegisterDialog.Saving">
												Saving...
											</Translate>
										}
										sx={{
											minHeight: { xs: 44, sm: 36 },
										}}
									>
										<Translate String="Shift.Event.Save">Save</Translate>
									</SaveActionButton>
								</DialogActions>
						</Form>
					);
				}}
			</Formik>
		</Dialog>
	);
};

export default ShiftRegisterDialog;
